import React, { useCallback, useEffect, useState } from 'react';
import { Users, X } from 'lucide-react';
import api from '@/lib/api';

function errMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

// The people on one website: its owner and any editors. The owner (or Admin)
// adds and removes editors; an editor sees the list and can leave.
export function WebsitePeople({ website, canManage: canManageHint }) {
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const load = useCallback(async () => {
    try {
      const res = await api.getWebsiteMembers(website.id);
      setTeam(res.data);
    } catch (err) {
      setNote(errMessage(err, 'Could not load the people on this website.'));
    }
  }, [website.id]);
  useEffect(() => { if (open && !team) load(); }, [open, team, load]);
  const canManage = team ? team.can_manage : canManageHint;

  const add = async (e) => {
    e.preventDefault();
    const next = email.trim();
    if (!next) return;
    setBusy(true); setNote('');
    try {
      const res = await api.addWebsiteMember(website.id, { email: next, send_invite: true });
      setTeam(t => ({ ...(t || {}), ...res.data }));
      setEmail('');
      setNote(res.data.invited ? `Added ${next} and sent an invitation.` : `Added ${next}. The invitation could not be sent — they can sign in with "Forgot password".`);
    } catch (err) {
      setNote(errMessage(err, 'Could not add that person.'));
    }
    setBusy(false);
  };

  const remove = async (who, leaving) => {
    if (!window.confirm(leaving ? `Leave ${website.site_name || 'this website'}? You won't be able to edit it.` : `Remove ${who} from ${website.site_name || 'this website'}?`)) return;
    setBusy(true); setNote('');
    try {
      const res = await api.removeWebsiteMember(website.id, who);
      if (leaving) { window.location.reload(); return; }
      setTeam(t => ({ ...(t || {}), ...res.data }));
    } catch (err) {
      setNote(errMessage(err, 'Could not remove that person.'));
    }
    setBusy(false);
  };

  return (
    <div data-testid={`people-${website.id}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-300 min-h-[32px]" data-testid={`people-toggle-${website.id}`}>
        <Users className="w-3.5 h-3.5" /> {open ? 'Hide people' : (canManage === false ? 'People on this website' : 'People who can edit')}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
          {!team ? <p className="text-xs text-slate-500">Loading…</p> : (
            <ul className="space-y-1.5 text-xs">
              <li className="flex items-center justify-between gap-2">
                <span className="text-slate-200 break-all">{team.owner?.email || 'Owner'}</span>
                <span className="text-[10px] uppercase tracking-wide text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded">Owner</span>
              </li>
              {(team.members || []).map(m => {
                const leaving = team.your_role === 'editor';
                return (
                  <li key={m.email} className="flex items-center justify-between gap-2">
                    <span className="text-slate-300 break-all">{m.email}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide text-indigo-300 bg-indigo-500/15 px-1.5 py-0.5 rounded">Editor</span>
                      {(canManage || leaving) && (
                        <button type="button" onClick={() => remove(m.email, leaving)} disabled={busy} className="text-slate-500 hover:text-red-400 disabled:opacity-40" aria-label={`Remove ${m.email}`} data-testid={`people-remove-${website.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
              {(team.members || []).length === 0 && <li className="text-slate-500">No one else yet.</li>}
            </ul>
          )}
          {canManage && (
            <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 pt-1">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="their@email.com" aria-label="Email to add" className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded-lg h-10 px-3 text-xs text-slate-100 placeholder:text-slate-500 outline-none" data-testid={`people-email-${website.id}`} />
              <button type="submit" disabled={busy || !email.trim()} className="bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 px-3 min-h-[40px] rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`people-add-${website.id}`}>
                {busy ? 'Adding…' : 'Add editor'}
              </button>
            </form>
          )}
          {canManage && <p className="text-[11px] text-slate-500">Editors can change and publish this website. Only the owner can pay for it, give it away, or take it offline.</p>}
          {note && <p className="text-xs text-slate-400">{note}</p>}
        </div>
      )}
    </div>
  );
}

